import { useState, useEffect, useCallback } from 'react';
import { getSiteConfig } from '../utils/site';

// Extend the window type for the beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'rk_install_dismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

const isPWA =
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as unknown as { standalone?: boolean }).standalone === true;

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isAndroid(): boolean {
  return /Android/.test(navigator.userAgent);
}

function isMobile(): boolean {
  return isIOS() || isAndroid();
}

function isDismissed(): boolean {
  const val = localStorage.getItem(DISMISS_KEY);
  if (!val) return false;
  const ts = parseInt(val, 10);
  if (isNaN(ts)) return false;
  return Date.now() - ts < DISMISS_DURATION;
}

export default function InstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);
  const appName = getSiteConfig().appName;

  // Capture the beforeinstallprompt event (Android/Chrome)
  // Only preventDefault when we're actually going to show the banner —
  // calling it unconditionally logs a console warning on every page.
  const shouldShow = !isPWA && isMobile() && !isDismissed();

  useEffect(() => {
    if (!shouldShow) return;

    setVisible(true);

    function handlePrompt(e: Event) {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    }

    window.addEventListener('beforeinstallprompt', handlePrompt);
    return () => window.removeEventListener('beforeinstallprompt', handlePrompt);
  }, [shouldShow]);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setVisible(false);
      }
    } finally {
      setInstalling(false);
      setDeferredPrompt(null);
    }
  }, [deferredPrompt]);

  if (!visible || isPWA) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 200,
      background: 'var(--surface)', borderTop: '1px solid var(--border)',
      padding: '12px 16px',
      boxShadow: '0 -2px 12px rgba(0,0,0,0.15)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        maxWidth: 600, margin: '0 auto',
      }}>
        {/* App icon */}
        <img
          src="/icons/icon-192.png"
          alt=""
          style={{ width: 40, height: 40, borderRadius: 8, flexShrink: 0 }}
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
            Install {appName}
          </div>
          {isIOS() ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>
              Tap <ShareIcon /> then <strong>Add to Home Screen</strong>
            </div>
          ) : deferredPrompt ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Add to your home screen for quick access
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>
              Tap <MenuIcon /> then <strong>Add to Home screen</strong>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {!isIOS() && deferredPrompt && (
            <button
              onClick={install}
              disabled={installing}
              style={{
                padding: '8px 16px', borderRadius: 'var(--radius)',
                background: 'var(--accent)', color: '#fff', border: 'none',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {installing ? 'Installing...' : 'Install'}
            </button>
          )}
          <button
            onClick={dismiss}
            style={{
              padding: '8px 12px', borderRadius: 'var(--radius)',
              background: 'transparent', color: 'var(--text-muted)',
              border: 'none', fontSize: 18, cursor: 'pointer',
              lineHeight: 1,
            }}
            title="Dismiss"
          >
            &times;
          </button>
        </div>
      </div>
    </div>
  );
}

/** iOS share icon (square with arrow) */
function ShareIcon() {
  return (
    <svg
      width="14" height="14" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      style={{ verticalAlign: 'middle', margin: '0 2px' }}
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

/** Chrome menu icon (three vertical dots) */
function MenuIcon() {
  return (
    <svg
      width="14" height="14" viewBox="0 0 24 24"
      fill="currentColor"
      style={{ verticalAlign: 'middle', margin: '0 2px' }}
    >
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  );
}
